import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./document.css";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import axios from "axios";
import { useParams } from "react-router";

const initialDocuments = [
  { id: "claim-form", name: "Claim Form" },
  { id: "policy-document", name: "Policy" },
  { id: "registration", name: "RC" },
  { id: "license", name: "License" },
  { id: "workshop-estimate", name: "Workshop Work Estimate" },
];

const initialAdditionalDocuments = [
  { id: "document-1", name: "Document 1" },
  { id: "document-2", name: "Document 2" },
  { id: "document-3", name: "Document 3" },
];

const isAllowedDocumentFile = (file) => {
  const name = (file?.name || "").toLowerCase();
  const extension = name.includes(".") ? name.split(".").pop() : "";
  const allowedExtensions = ["pdf", "jpg", "jpeg", "png", "xls", "xlsx"];

  return allowedExtensions.includes(extension);
};

// UploadModal Component
const UploadModal = ({
  isOpen,
  onClose,
  docId,
  docName,
  uploadedFiles,
  onUploadComplete,
  claimNumber,
}) => {
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploadMode, setUploadMode] = useState("modal");

  const documentSubTypes = useMemo(() => {
    const subTypesConfig = {
      "policy-document": [
        { id: "policy-front", name: "Policy Front" },
        { id: "policy-back", name: "Policy Back" },
        { id: "terms-conditions", name: "Terms & Conditions" },
        { id: "schedule", name: "Schedule" },
        { id: "endorsements", name: "Endorsements" },
      ],
      registration: [
        { id: "rc-front", name: "RC Front" },
        { id: "rc-back", name: "RC Back" },
        { id: "vehicle-photo", name: "Vehicle Photo" },
        { id: "chassis-photo", name: "Chassis Photo" },
      ],
      license: [
        { id: "license-front", name: "License Front" },
        { id: "license-back", name: "License Back" },
        { id: "license-photo", name: "License Photo" },
      ],
      "workshop-estimate": [
        { id: "estimate-front", name: "Estimate Front" },
        { id: "estimate-back", name: "Estimate Back" },
        { id: "damage-assessment", name: "Damage Assessment" },
        { id: "parts-list", name: "Parts List" },
        { id: "labor-charges", name: "Labor Charges" },
      ],
    };

    return (
      subTypesConfig[docId] || [
        { id: "main-document", name: "Main Document" },
        { id: "additional-1", name: "Additional Page 1" },
        { id: "additional-2", name: "Additional Page 2" },
      ]
    );
  }, [docId]);

  useEffect(() => {
    if (isOpen) {
      setFiles({});
      setPreviewMode(false);
      setUploadMode("modal");
      if (uploadedFiles && uploadedFiles.length > 0) {
        const organizedFiles = {};
        const defaultSubType = documentSubTypes[0]?.id || "main-document";

        uploadedFiles.forEach((file) => {
          const subType = file.subType || defaultSubType;
          if (!organizedFiles[subType]) {
            organizedFiles[subType] = [];
          }
          organizedFiles[subType].push(file);
        });
        setFiles(organizedFiles);
        setPreviewMode(true);
      }
    }
  }, [isOpen, uploadedFiles]);

  const getDocumentContentType = (file) => {
    const extension = (file?.name || "").split(".").pop()?.toLowerCase();

    switch (extension) {
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      case "xls":
        return "application/vnd.ms-excel";
      default:
        return file?.type || "application/octet-stream";
    }
  };

  const handleFileSelect = (event, subTypeId) => {
    const selectedFiles = Array.from(event.target.files || []);
    const invalidFiles = selectedFiles.filter(
      (file) => !isAllowedDocumentFile(file),
    );

    if (invalidFiles.length > 0) {
      alert("Only PDF, JPG, JPEG, PNG, XLS, and XLSX files are allowed.");
    }

    const validFiles = selectedFiles.filter((file) =>
      isAllowedDocumentFile(file),
    );
    if (validFiles.length === 0) return;

    const newFiles = validFiles.map((file, index) => ({
      id: `${subTypeId}_${Date.now()}_${index}`,
      file: file,
      name: file.name,
      type: file.type,
      size: file.size,
      subType: subTypeId,
      uploadTime: new Date().toLocaleString(),
      previewUrl: URL.createObjectURL(file),
    }));

    setFiles((prev) => ({
      ...prev,
      [subTypeId]: [...(prev[subTypeId] || []), ...newFiles],
    }));
  };

  const uploadFileToServer = async (fileObj) => {
    const file = fileObj.file;
    const subType = fileObj.subType;
    const fileExtension = file.name.split(".").pop();
    const uniqueFileName = `${claimNumber}_${docId}_${subType}_${Date.now()}.${fileExtension}`;
    const blobName = `claimDocuments/${uniqueFileName}`;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = async () => {
        const contentType = getDocumentContentType(file);
        const base64String = reader.result.split(",")[1];

        try {
          const response = await axios.post("/process_data/upload-file", {
            blobName,
            fileContent: base64String,
            contentType,
          });

          resolve({
            ...fileObj,
            serverUrl: response.data.url,
            blobName: blobName,
            uploaded: true,
            uploadedTime: new Date().toLocaleString(),
          });
        } catch (error) {
          reject(error);
        }
      };

      reader.readAsDataURL(file);
    });
  };

  const handleUploadAll = async () => {
    const allFiles = [];
    Object.values(files).forEach((fileArray) => {
      allFiles.push(...fileArray);
    });

    if (allFiles.length === 0) return;

    setUploading(true);

    try {
      const uploadedResults = [];
      for (const fileObj of allFiles) {
        const result = await uploadFileToServer(fileObj);
        uploadedResults.push(result);
      }

      onUploadComplete(docId, uploadedResults);
      setPreviewMode(true);
      setUploadMode("modal");
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Some files failed to upload. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (subTypeId, fileId) => {
    setFiles((prev) => {
      const updatedFiles = { ...prev };
      if (updatedFiles[subTypeId]) {
        updatedFiles[subTypeId] = updatedFiles[subTypeId].filter(
          (f) => f.id !== fileId,
        );
        if (updatedFiles[subTypeId].length === 0) {
          delete updatedFiles[subTypeId];
        }
      }
      return updatedFiles;
    });
  };

  const handlePreviewFile = (fileUrl) => {
    window.open(fileUrl, "_blank");
  };

  const clearAllFiles = () => {
    setFiles({});
  };

  const handleDirectUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!isAllowedDocumentFile(file)) {
      alert("Only PDF, JPG, JPEG, PNG, XLS, and XLSX files are allowed.");
      return;
    }

    const reader = new FileReader();

    reader.onloadend = async () => {
      const fileExtension = file.name.split(".").pop();
      const contentType = getDocumentContentType(file);
      const uniqueFileName = `${claimNumber}_${docId}_${Date.now()}.${fileExtension}`;
      const blobName = `claimDocuments/${uniqueFileName}`;
      const base64String = reader.result.split(",")[1];

      setUploading(true);

      try {
        const response = await axios.post("/process_data/upload-file", {
          blobName,
          fileContent: base64String,
          contentType,
        });

        if (response.data.url) {
          const uploadedFile = {
            id: `${docId}_${Date.now()}`,
            name: file.name,
            type: contentType,
            serverUrl: response.data.url,
            uploadedTime: new Date().toLocaleString(),
            subType: "main-document",
          };

          onUploadComplete(docId, [uploadedFile]);
          setPreviewMode(true);
        }
      } catch (error) {
        console.error("Direct upload failed:", error);
        alert("Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{docName}</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {!previewMode && (
          <div className="upload-mode-selector">
            <button
              className={`mode-btn ${uploadMode === "modal" ? "active" : ""}`}
              onClick={() => setUploadMode("modal")}
            >
              Detailed Upload (Multi-page)
            </button>
            <button
              className={`mode-btn ${uploadMode === "direct" ? "active" : ""}`}
              onClick={() => setUploadMode("direct")}
            >
              Quick Upload (Single file)
            </button>
          </div>
        )}

        {!previewMode ? (
          <>
            {uploadMode === "modal" ? (
              <>
                <div className="upload-section">
                  <div className="sub-buttons-container">
                    {documentSubTypes.map((subType) => (
                      <div key={subType.id} className="sub-button-group">
                        <div className="sub-button-header">
                          <span className="sub-button-name">
                            {subType.name}
                          </span>
                          {files[subType.id] &&
                            files[subType.id].length > 0 && (
                              <span className="file-count-badge">
                                {files[subType.id].length} file
                                {files[subType.id].length !== 1 ? "s" : ""}
                              </span>
                            )}
                        </div>

                        <div className="sub-button-actions">
                          <input
                            type="file"
                            id={`file-input-${subType.id}`}
                            accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                            onChange={(e) => handleFileSelect(e, subType.id)}
                            style={{ display: "none" }}
                            multiple
                          />
                          <button
                            className="sub-upload-btn"
                            onClick={() =>
                              document
                                .getElementById(`file-input-${subType.id}`)
                                .click()
                            }
                            disabled={uploading}
                          >
                            <FaCloudUploadAlt /> Upload
                          </button>

                          {files[subType.id] &&
                            files[subType.id].map((fileObj) => (
                              <div
                                key={fileObj.id}
                                className="uploaded-file-item"
                              >
                                <span className="file-name">
                                  {fileObj.name}
                                </span>
                                <button
                                  className="remove-file-btn"
                                  onClick={() =>
                                    removeFile(subType.id, fileObj.id)
                                  }
                                  title="Remove file"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="modal-actions">
                    <button
                      className="upload-all-btn"
                      onClick={handleUploadAll}
                      disabled={Object.keys(files).length === 0 || uploading}
                    >
                      {uploading ? (
                        <>
                          <ImSpinner2 className="spinner" /> Uploading...
                        </>
                      ) : (
                        `Upload All Files`
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="new-upload-ui">
                  <div className="direct-upload-section">
                    <input
                      type="file"
                      id="direct-file-input"
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                      onChange={handleDirectUpload}
                      style={{ display: "none" }}
                    />
                    <div className="direct-upload-box">
                      <FaCloudUploadAlt size={48} className="upload-icon" />
                      <p className="upload-text">Click to upload {docName}</p>
                      <p className="upload-hint">
                        Supports PDF, JPG, PNG, XLS, and XLSX files
                      </p>
                      <button
                        className="direct-upload-btn"
                        onClick={() =>
                          document.getElementById("direct-file-input").click()
                        }
                        disabled={uploading}
                      >
                        {uploading ? (
                          <>
                            <ImSpinner2 className="spinner" /> Uploading...
                          </>
                        ) : (
                          "Choose File"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="preview-section">
              <div className="uploaded-files-count">
                <span className="file-count-badge">
                  {Object.values(files).reduce(
                    (total, fileArray) => total + fileArray.length,
                    0,
                  )}{" "}
                  files
                </span>
                uploaded for {docName}
              </div>

              <div className="preview-sections-container">
                {documentSubTypes.map(
                  (subType) =>
                    files[subType.id] &&
                    files[subType.id].length > 0 && (
                      <div key={subType.id} className="preview-sub-section">
                        <div className="preview-sub-header">
                          <h4>{subType.name}</h4>
                          <span className="sub-file-count">
                            {files[subType.id].length} files
                          </span>
                        </div>

                        <div className="preview-files-list">
                          {files[subType.id].map((fileObj, index) => (
                            <div
                              key={fileObj.id || index}
                              className="preview-file-item"
                            >
                              <div className="preview-file-info">
                                <span className="preview-file-name">
                                  File {index + 1}: {fileObj.name}
                                </span>
                                <span className="preview-file-time">
                                  {fileObj.uploadTime || fileObj.uploadedTime}
                                </span>
                              </div>

                              <div className="preview-file-actions">
                                <button
                                  className="view-original-btn"
                                  onClick={() =>
                                    fileObj.serverUrl &&
                                    handlePreviewFile(fileObj.serverUrl)
                                  }
                                >
                                  Preview
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                )}
              </div>

              {Object.keys(files).length === 0 && (
                <div className="no-files-message">
                  No files uploaded yet for {docName}
                </div>
              )}
            </div>
          </>
        )}

        <div className="modal-footer">
          {previewMode ? (
            <button
              className="back-to-upload-btn"
              onClick={() => setPreviewMode(false)}
            >
              ← Upload More Files
            </button>
          ) : (
            uploadMode === "modal" && (
              <button
                className="back-to-upload-btn"
                onClick={clearAllFiles}
                disabled={Object.keys(files).length === 0}
              >
                Clear All
              </button>
            )
          )}
          <button className="close-modal-btn" onClick={onClose}>
            {previewMode ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Documents Modal Component - UPDATED
const MainDocumentsModal = ({
  isOpen,
  onClose,
  claimNumber,
  onUploadComplete,
  parentDocId,
  parentDocName,
  uploadedDocs, // यहाँ सिर्फ URL strings हैं, file objects नहीं
  uploadedDocuments,
}) => {
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [uploadedCustomDocs, setUploadedCustomDocs] = useState({});
  const [isCheckingDatabase, setIsCheckingDatabase] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");

  // Custom titles for each document type
  const getModalTitle = () => {
    const titleMap = {
      "claim-form": "Upload Additional Claim Forms",
      "policy-document": "Upload Policy Supporting Documents",
      registration: "Upload Vehicle Registration Documents",
      license: "Upload Driving License Documents",
      "workshop-estimate": "Upload Workshop Estimate Details",
    };

    return titleMap[parentDocId] || `Upload Documents for ${parentDocName}`;
  };

  // Custom document names for each document type - UPDATED
  const getDocumentDisplayName = (docId) => {
    // Map document types to their display names
    const docTypeMap = {
      "claim-form": "Claim form",
      "policy-document": "Policy",
      registration: "RC",
      license: "License",
      "workshop-estimate": "Workshop Estimate",
    };

    // Map docId to DOC number
    const docNumberMap = {
      "document-1a": "-1",
      "document-2a": "-2",
      "document-3a": "-3",
      "document-4a": "-4",
      "document-5a": "-5",
    };

    const docType = docTypeMap[parentDocId] || "DOCUMENT";
    const docNumber = docNumberMap[docId] || "-XX";

    // Return format: "FORM DOC-1A"
    return `${docType} ${docNumber}`;
  };

  // Reset state when modal opens for a different document
  useEffect(() => {
    if (isOpen && claimNumber && parentDocId) {
      console.log("📂 Modal opened, checking database for existing files...");
      checkDatabaseForExistingFiles();
    }
  }, [isOpen, claimNumber, parentDocId]);

  useEffect(() => {
    if (uploadMessage) {
      const timer = setTimeout(() => {
        setUploadMessage("");
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [uploadMessage]);

  // Handle file upload
  const handleFileUpload = async (event, docId) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!isAllowedDocumentFile(file)) {
      alert("Only PDF, JPG, JPEG, PNG, XLS, and XLSX files are allowed.");
      event.target.value = "";
      return;
    }

    const uniqueKey = `${parentDocId}_${docId}`;
    setUploadingDoc(uniqueKey);

    const reader = new FileReader();

    reader.onloadend = async () => {
      const fileExtension = file.name.split(".").pop();
      const contentType = file.type;

      const fileName = `${claimNumber}_${parentDocId}_${docId}.${fileExtension}`;
      const blobName = `claimDocuments/${fileName}`;
      const base64String = reader.result.split(",")[1];

      try {
        const response = await axios.post("/process_data/upload-file", {
          blobName,
          fileContent: base64String,
          contentType,
        });

        if (response.status === 200 || response.status === 201) {
          const uploadedUrl = response.data.url;
          const currentTime = new Date().toLocaleString();

          // Store locally with unique key
          const fileInfo = {
            url: uploadedUrl,
            time: currentTime,
            name: file.name,
            parentDocId: parentDocId,
            docId: docId,
            // Add additional properties for preview
            serverUrl: uploadedUrl,
            uploadedTime: currentTime,
            type: contentType,
          };

          setUploadedCustomDocs((prev) => ({
            ...prev,
            [uniqueKey]: fileInfo,
          }));

          // Call parent callback with additional info
          if (onUploadComplete) {
            onUploadComplete(uniqueKey, [
              {
                id: `${uniqueKey}_${Date.now()}`,
                name: file.name,
                type: contentType,
                serverUrl: uploadedUrl,
                uploadedTime: currentTime,
                subType: "main-document",
                parentDocId: parentDocId,
                docId: docId,
              },
            ]);
          }

          // Custom success message based on document type
          const successMessages = {
            "claim-form": `✅ CLAIM FORM ${docId.toUpperCase().replace("A", "")} uploaded successfully!`,
            "policy-document": `✅POLICY ${docId.toUpperCase().replace("A", "")} uploaded successfully!`,
            registration: `✅RC ${docId.toUpperCase().replace("A", "")} uploaded successfully!!`,
            license: `✅LICENSE ${docId.toUpperCase().replace("A", "")} uploaded successfully!!`,
            "workshop-estimate": `✅ WORKSHOP ESTIMATE ${docId.toUpperCase().replace("A", "")} uploaded successfully!!`,
          };

          // alert(
          //   successMessages[parentDocId] ||
          //     `✅ ${file.name} uploaded successfully for ${parentDocName}!`,
          // );
          setUploadMessage(
            successMessages[parentDocId] ||
              `✅ ${file.name} uploaded successfully`,
          );
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        alert("Failed to upload file. Please try again.");
      } finally {
        setUploadingDoc(null);
      }
    };

    reader.readAsDataURL(file);
  };

  // Check if specific document is uploaded
  const isDocumentUploaded = (docId) => {
    const uniqueKey = `${parentDocId}_${docId}`;

    // Check all possible states in order
    // 1. Local modal state (recent uploads in current session)
    if (uploadedCustomDocs[uniqueKey]?.url) {
      console.log(`✅ ${uniqueKey}: Found in uploadedCustomDocs`);
      return true;
    }

    // 2. Parent URL strings state
    if (uploadedDocs?.[uniqueKey]) {
      console.log(`✅ ${uniqueKey}: Found in uploadedDocs`);
      return true;
    }

    // 3. Parent full file objects state
    if (uploadedDocuments?.[uniqueKey]?.[0]?.serverUrl) {
      console.log(`✅ ${uniqueKey}: Found in uploadedDocuments`);
      return true;
    }

    // 4. Check localStorage (only if you really need it)
    try {
      const localStorageKey = `doc_${claimNumber}_${uniqueKey}`;
      const storedDoc = localStorage.getItem(localStorageKey);
      if (storedDoc) {
        console.log(`✅ ${uniqueKey}: Found in localStorage`);
        return true;
      }
    } catch (e) {
      // Silent fail for localStorage
    }

    console.log(`❌ ${uniqueKey}: Not found in any state`);
    return false;
  };

  //  NEW FUNCTION after existing functions
  const checkDatabaseForExistingFiles = async () => {
    setIsCheckingDatabase(true);
    console.log("🔍 Starting database check for custom documents...");

    // Custom document IDs check krega
    const customDocIds = [
      "document-1a",
      "document-2a",
      "document-3a",
      "document-4a",
      "document-5a",
    ];

    for (const docId of customDocIds) {
      const uniqueKey = `${parentDocId}_${docId}`;

      console.log(`Checking: ${uniqueKey}`);

      // Pehle check krega agar already loaded hai
      if (uploadedDocs?.[uniqueKey] || uploadedDocuments?.[uniqueKey]) {
        console.log(`Already loaded: ${uniqueKey}`);
        continue;
      }

      // Database se check karega
      try {
        const blobName = `claimDocuments/${claimNumber}_${uniqueKey}`;
        const response = await axios.post(
          "/process_data/check_document_exists",
          {
            blobName: blobName,
          },
        );

        if (response.data.exists && response.data.url) {
          console.log(`669 Found in database: ${uniqueKey}`);

          // Update local state
          const fileInfo = {
            url: response.data.url,
            time: "Previously uploaded",
            name: `${uniqueKey}`,
            parentDocId: parentDocId,
            docId: docId,
          };

          setUploadedCustomDocs((prev) => ({
            ...prev,
            [uniqueKey]: fileInfo,
          }));

          // Parent ko inform kare (optional)
          if (onUploadComplete) {
            onUploadComplete(uniqueKey, [
              {
                id: `${uniqueKey}_existing_${Date.now()}`,
                name: fileInfo.name,
                type: response.data.url.includes(".pdf")
                  ? "application/pdf"
                  : "image/*",
                serverUrl: response.data.url,
                uploadedTime: fileInfo.time,
                subType: "main-document",
                parentDocId: parentDocId,
                docId: docId,
              },
            ]);
          }
        }
      } catch (error) {
        console.error(`Error checking ${uniqueKey}:`, error.message);
      } finally {
        setIsCheckingDatabase(false); //  STOP loading
      }
    }

    console.log(" Database check completed");
  };

  const checkS3ForExistingFiles = async () => {
    console.log(" Checking S3 for existing files...");

    try {
      const response = await axios.post("/process_data/check-s3-files", {
        claimNumber: claimNumber,
        parentDocId: parentDocId,
      });

      console.log("S3 Response:", response.data);

      if (response.data.success) {
        const s3Files = {};

        response.data.files.forEach((file) => {
          if (file.exists && file.uniqueKey) {
            s3Files[file.uniqueKey] = {
              url: file.url,
              time: new Date().toLocaleString(),
              name: file.fileName,
              parentDocId: parentDocId,
              docId: file.docId,
              source: "S3",
            };

            console.log(` Loaded from S3: ${file.uniqueKey} -> ${file.url}`);
          }
        });

        // Update state
        setUploadedCustomDocs((prev) => ({
          ...prev,
          ...s3Files,
        }));

        console.log(`📊 Loaded ${Object.keys(s3Files).length} files from S3`);
      }
    } catch (error) {
      console.error("❌ Error checking S3:", error);
    }
  };

  //  use effect use kia document k lie
  useEffect(() => {
    if (isOpen && claimNumber && parentDocId) {
      console.log("Modal opened, checking S3...");
      checkS3ForExistingFiles(); // existing file check krne k lie
    }
  }, [isOpen, claimNumber, parentDocId]);

  // Handle preview click - FIXED VERSION
  const handlePreviewClick = (docId) => {
    const uniqueKey = `${parentDocId}_${docId}`;

    // First check local state
    const localDocInfo = uploadedCustomDocs[uniqueKey];
    if (localDocInfo && localDocInfo.url) {
      console.log("Opening local file:", localDocInfo.url);
      window.open(localDocInfo.url, "_blank", "noopener,noreferrer");
      return;
    }

    // Then check parent state (uploadedDocs contains URL strings)
    const parentFileUrl =
      typeof uploadedDocs?.[uniqueKey] === "string"
        ? uploadedDocs[uniqueKey]
        : uploadedDocuments?.[uniqueKey]?.[0]?.serverUrl;
    if (parentFileUrl) {
      console.log("Opening parent file:", parentFileUrl);
      window.open(parentFileUrl, "_blank", "noopener,noreferrer");
      return;
    }

    console.error("No file found for preview:", uniqueKey);
    alert("File not found for preview. Please re-upload the document.");
  };

  // Get file info for display
  const getFileInfo = (docId) => {
    const uniqueKey = `${parentDocId}_${docId}`;

    // First check local state
    if (uploadedCustomDocs[uniqueKey]) {
      return uploadedCustomDocs[uniqueKey];
    }

    // Then check parent state
    if (uploadedDocs && uploadedDocs[uniqueKey]) {
      // Create a basic file info object from URL
      return {
        url: uploadedDocs[uniqueKey],
        time: "Previously uploaded",
        name: `${parentDocId}_${docId}`,
        parentDocId: parentDocId,
        docId: docId,
      };
    }

    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content main-documents-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>{getModalTitle()}</h3>
          <div className="modal-close-addt">
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        {uploadMessage && <div className="upload-toast">{uploadMessage}</div>}

        <div className="documents-grid-modal">
          {/* First Row */}
          <div className="grid-row">
            {["document-1a", "document-2a", "document-3a"].map((docId) => {
              const fileInfo = getFileInfo(docId);
              const isUploaded = isDocumentUploaded(docId);

              return (
                <div className="document-card-modal" key={docId}>
                  <span className="document-name-modal">
                    {getDocumentDisplayName(docId)}
                  </span>

                  {/* {isUploaded && fileInfo && (
                    <div className="file-info-small">
                      <span className="file-name-small" title={fileInfo.name}>
                        {fileInfo.name.length > 15 
                          ? `${fileInfo.name.substring(0, 15)}...` 
                          : fileInfo.name}
                      </span>
                      <span className="file-time-small">{fileInfo.time}</span>
                    </div>
                  )} */}

                  {isUploaded ? (
                    <button
                      className="upload-btn-modal preview-mode"
                      onClick={() => handlePreviewClick(docId)}
                      title={fileInfo ? `Preview: ${fileInfo.name}` : "Preview"}
                    >
                      Preview
                    </button>
                  ) : (
                    <>
                      <input
                        type="file"
                        id={`${parentDocId}_${docId}_hidden-input`}
                        style={{ display: "none" }}
                        accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                        onChange={(e) => handleFileUpload(e, docId)}
                      />
                      <button
                        className="upload-btn-modal"
                        onClick={() =>
                          document
                            .getElementById(
                              `${parentDocId}_${docId}_hidden-input`,
                            )
                            .click()
                        }
                        disabled={uploadingDoc === `${parentDocId}_${docId}`}
                      >
                        {uploadingDoc === `${parentDocId}_${docId}` ? (
                          <>
                            <ImSpinner2 className="spinner" /> Uploading...
                          </>
                        ) : (
                          <>
                            <FaCloudUploadAlt /> Upload
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Second Row */}
          <div className="grid-row">
            {["document-4a", "document-5a"].map((docId) => {
              const fileInfo = getFileInfo(docId);
              const isUploaded = isDocumentUploaded(docId);

              return (
                <div className="document-card-modal" key={docId}>
                  <span className="document-name-modal">
                    {getDocumentDisplayName(docId)}
                  </span>

                  {/* {isUploaded && fileInfo && (
                    <div className="file-info-small">
                      <span className="file-name-small" title={fileInfo.name}>
                        {fileInfo.name.length > 15 
                          ? `${fileInfo.name.substring(0, 15)}...` 
                          : fileInfo.name}
                      </span>
                      <span className="file-time-small">{fileInfo.time}</span>
                    </div>
                  )} */}

                  {isUploaded ? (
                    <button
                      className="upload-btn-modal preview-mode"
                      onClick={() => handlePreviewClick(docId)}
                      title={fileInfo ? `Preview: ${fileInfo.name}` : "Preview"}
                    >
                      Preview
                    </button>
                  ) : (
                    <>
                      <input
                        type="file"
                        id={`${parentDocId}_${docId}_hidden-input`}
                        style={{ display: "none" }}
                        accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                        onChange={(e) => handleFileUpload(e, docId)}
                      />
                      <button
                        className="upload-btn-modal"
                        onClick={() =>
                          document
                            .getElementById(
                              `${parentDocId}_${docId}_hidden-input`,
                            )
                            .click()
                        }
                        disabled={uploadingDoc === `${parentDocId}_${docId}`}
                      >
                        {uploadingDoc === `${parentDocId}_${docId}` ? (
                          <>
                            <ImSpinner2 className="spinner" /> Uploading...
                          </>
                        ) : (
                          <>
                            <FaCloudUploadAlt /> Upload
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const DocumentStatus = () => {
  const { claimNumber } = useParams();
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploadTimes, setUploadTimes] = useState({});
  const [uploadingDocIds, setUploadingDocIds] = useState([]);
  const [additionalDocuments, setAdditionalDocuments] = useState(
    initialAdditionalDocuments,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [mainDocumentsModalOpen, setMainDocumentsModalOpen] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [processingStage, setProcessingStage] = useState("");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingError, setProcessingError] = useState(null);
  const [docStatus, setDocStatus] = useState(false);
  const [docProcessingStatus, setDocProcessingStatus] = useState(false);
  const [isCheckingDocuments, setIsCheckingDocuments] = useState(true);
  const [surveyDocUpdated, setSurveyDocUpdated] = useState(false);
  const [uploadedFileNames, setUploadedFileNames] = useState({});
  const [emailSent, setEmailSent] = useState(false);

  // State to track which main document's modal is open
  const [currentMainDoc, setCurrentMainDoc] = useState(null);

  const areMainDocumentsUploaded = useMemo(() => {
    return initialDocuments.every((mainDoc) => {
      // Option 1: Main document itself has files
      // console.log("984", mainDoc);
      console.log(uploadedDocs, uploadedDocuments);

      const hasMainFile =
        uploadedDocs[mainDoc.id] || uploadedDocuments[mainDoc.id];

      // Option 2: Has custom documents uploaded
      const customDocIds = [
        "document-1a",
        "document-2a",
        "document-3a",
        "document-4a",
        "document-5a",
      ];

      const hasCustomFiles = customDocIds.some((customId) => {
        const uniqueKey = `${mainDoc.id}_${customId}`;
        // console.log(uni);

        return uploadedDocs[uniqueKey] || uploadedDocuments[uniqueKey];
      });
      console.log(hasMainFile, hasCustomFiles);

      return hasMainFile || hasCustomFiles;
    });
  }, [uploadedDocs, uploadedDocuments]);

  // Fetch document status
  // useEffect(() => {
  //   if (!claimNumber) return

  //   const fetchDocStatus = async () => {
  //     try {
  //       const response = await axios.get('/process_data/get-doc-status', {
  //         params: { claimNumber },
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem('token')}`
  //         }
  //       })

  //       if (response.data.success) {
  //         setDocStatus(response.data.jsonDocStatus)
  //         setDocProcessingStatus(response.data.docProcessingStatus)
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch doc status:', error)
  //     }
  //   }

  //   fetchDocStatus()
  // }, [claimNumber])

  useEffect(() => {
    if (!claimNumber) return;

    const fetchDocStatus = async () => {
      try {
        const response = await axios.get("/process_data/get-doc-status", {
          params: { claimNumber },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        console.log("1035 Doc Status API Response:", response.data);

        if (response.data.success) {
          setDocStatus(response.data.jsonDocStatus);
          setDocProcessingStatus(response.data.docProcessingStatus);
        }
      } catch (error) {
        console.error("Failed to fetch doc status:", error);
      }
    };

    fetchDocStatus();
  }, [claimNumber]);

  const checkDocumentExistence = async (docId) => {
    // console.log(`Quick checking for: ${docId}`);

    // सबसे common patterns पहले check करें
    const patterns = [
      `${claimNumber}_${docId}`,
      docId.includes("_") ? docId : `${claimNumber}_${docId}`,
    ];

    const extensions = ["pdf", "jpg", "jpeg", "png"];

    // सभी combinations के लिए parallel calls
    const checkPromises = [];

    for (const pattern of patterns) {
      for (const ext of extensions) {
        const blobName = `claimDocuments/${pattern}.${ext}`;
        checkPromises.push(
          axios
            .post(
              "/process_data/check_document_exists",
              { blobName },
              { timeout: 3000 }, // 3 seconds timeout - कम कर दिया
            )
            .then((response) => ({
              blobName,
              exists: response.data.exists,
              url: response.data.url,
            }))
            .catch((error) => ({
              blobName,
              exists: false,
              error: error.message,
            })),
        );
      }
    }

    const results = await Promise.all(checkPromises);

    // पहला valid result return करें
    for (const result of results) {
      if (result.exists && result.url) {
        console.log(`✓ Found: ${result.blobName}`);
        return result.url;
      }
    }

    // console.log(`✗ Not found: ${docId}`);
    return null;
  };

  useEffect(() => {
    if (!claimNumber) {
      console.log("No claim number found");
      return;
    }

    console.log("⏱️ Starting quick document check...");

    let ignore = false;

    const timer = setTimeout(async () => {
      const cacheKey = `survey_docs_${claimNumber}`;

      // ---------------- CACHE READ ----------------
      try {
        const cached = localStorage.getItem(cacheKey);

        if (cached) {
          const parsed = JSON.parse(cached);

          // 2 minute cache validity
          if (Date.now() - parsed.timestamp < 2 * 60 * 1000) {
            console.log("🚀 Using cached documents data");

            if (!ignore) {
              setUploadedDocs(parsed.uploadedDocs || {});
              setUploadTimes(parsed.uploadTimes || {});
              setUploadedDocuments(parsed.uploadedDocuments || {});
              setIsCheckingDocuments(false);
            }

            return; // STOP — no API call
          }

          console.log("🔄 Cache expired, fetching fresh data");
          localStorage.removeItem(cacheKey);
        }
      } catch (e) {
        console.log("Cache parse error:", e);
      }

      // ---------------- FETCH ----------------
      try {
        setIsCheckingDocuments(true);
        console.log("🔍 Fresh fetch for:", claimNumber);

        const response = await axios.post(
          "/process_data/get_survey_doc_status",
          { claimNumber },
          { timeout: 100000 },
        );

        if (!response.data?.survey_doc) {
          console.log("No survey doc for this claim.");
          return;
        }

        const updatedDocs = {};
        const updatedTimes = {};
        const updatedUploadedDocuments = {};

        const checkDocumentWithLogging = async (docId) => {
          const url = await checkDocumentExistence(docId);
          // console.log(url ? `✓ Found ${docId}` : `✗ Missing ${docId}`);
          return url;
        };

        const checkAndUpdate = async (
          docs,
          isCustom = false,
          parentId = null,
        ) => {
          const results = await Promise.all(
            docs.map(async (doc) => {
              const docId = isCustom ? `${parentId}_${doc}` : doc.id;

              const fileUrl = await checkDocumentWithLogging(docId);
              if (!fileUrl) return null;

              return { docId, fileUrl, isCustom, parentId, doc };
            }),
          );

          results.forEach((result) => {
            if (!result) return;

            const { docId, fileUrl, isCustom, parentId, doc } = result;

            updatedDocs[docId] = fileUrl;
            updatedTimes[docId] = new Date().toLocaleString();

            updatedUploadedDocuments[docId] = [
              {
                id: `${docId}_${Date.now()}`,
                name: docId,
                type: fileUrl.includes(".pdf") ? "application/pdf" : "image/*",
                serverUrl: fileUrl,
                uploadedTime: new Date().toLocaleString(),
                subType: "main-document",
                parentDocId: parentId,
                docId: isCustom ? doc : docId,
              },
            ];
          });
        };

        // -------- MAIN DOCS --------
        await checkAndUpdate(initialDocuments);

        // -------- ADDITIONAL DOCS --------
        await checkAndUpdate(initialAdditionalDocuments);

        // -------- CUSTOM DOCS --------
        const customDocIds = [
          "document-1a",
          "document-2a",
          "document-3a",
          "document-4a",
          "document-5a",
        ];

        for (const mainDoc of initialDocuments) {
          await checkAndUpdate(customDocIds, true, mainDoc.id);
        }

        if (ignore) return;

        // ---------------- STATE UPDATE + CACHE (SAFE) ----------------
        setUploadedDocs((prev) => {
          const newDocsState = { ...prev, ...updatedDocs };

          // save cache using latest state snapshot
          const cacheData = {
            uploadedDocs: newDocsState,
            uploadTimes: { ...updatedTimes },
            uploadedDocuments: { ...updatedUploadedDocuments },
            timestamp: Date.now(),
          };

          try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log("💾 Documents cached correctly");
          } catch (e) {
            console.log("Cache save failed:", e);
          }

          return newDocsState;
        });

        setUploadTimes((prev) => ({ ...prev, ...updatedTimes }));
        setUploadedDocuments((prev) => ({
          ...prev,
          ...updatedUploadedDocuments,
        }));
      } catch (err) {
        console.error("Error fetching documents:", err);
      } finally {
        if (!ignore) setIsCheckingDocuments(false);
        console.log("✅ Document fetch completed");
      }
    }, 150);

    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [claimNumber]);

  // Prevent page navigation during OCR processing
  // useEffect(() => {
  //   const handleBeforeUnload = (e) => {
  //     if (isProcessingOCR) {
  //       e.preventDefault();
  //       e.returnValue =
  //         "OCR processing is in progress. Are you sure you want to leave?";
  //       return e.returnValue;
  //     }
  //   };

  //   const handlePopState = (e) => {
  //     if (isProcessingOCR) {
  //       e.preventDefault();
  //       if (
  //         window.confirm(
  //           "OCR processing is in progress. Are you sure you want to leave?",
  //         )
  //       ) {
  //         return;
  //       } else {
  //         window.history.pushState(null, "", window.location.pathname);
  //       }
  //     }
  //   };

  //   if (isProcessingOCR) {
  //     window.addEventListener("beforeunload", handleBeforeUnload);
  //     window.addEventListener("popstate", handlePopState);
  //     window.history.pushState(null, "", window.location.pathname);
  //   }

  //   return () => {
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //     window.removeEventListener("popstate", handlePopState);
  //   };
  // }, [isProcessingOCR]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isProcessingOCR) {
        e.preventDefault();
        e.returnValue = "OCR processing is in progress.";
      }
    };

    if (isProcessingOCR) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isProcessingOCR]);

  // Check if document is additional document
  const isAdditionalDocument = (docId) => {
    return additionalDocuments.some((ad) => ad.id === docId);
  };

  // Handle upload click for custom documents - UPDATED
  const handleUploadClick = (doc) => {
    if (isAdditionalDocument(doc.id)) {
      document.getElementById(`${doc.id}-hidden-input`).click();
    } else {
      // Set current main document and open modal
      setCurrentMainDoc(doc);
      setMainDocumentsModalOpen(true);
    }
  };

  // Handle preview click for main documents
  // Handle preview click for main documents
  const handlePreviewClick = (doc) => {
    console.log(
      "📱 Preview clicked for main document:",
      claimNumber,
      doc.name,
      doc.id,
    );

    if (isAdditionalDocument(doc.id)) {
      // Additional documents के लिए direct preview
      const fileUrl =
        typeof uploadedDocs[doc.id] === "string"
          ? uploadedDocs[doc.id]
          : uploadedDocuments[doc.id]?.[0]?.serverUrl;
      if (fileUrl) {
        window.open(fileUrl, "_blank", "noopener,noreferrer");
      } else {
        alert("File not found. Please upload the document first.");
      }
    } else {
      // ✅ Main documents के लिए MainDocumentsModal open करें
      console.log("Opening MainDocumentsModal for:", claimNumber, doc.name);

      // Set current main document (यह important है!)
      setCurrentMainDoc(doc);

      // Open the modal
      setMainDocumentsModalOpen(true);

      // Debug log
      setTimeout(() => {
        console.log("MainDocumentsModal should be open now");
        console.log("currentMainDoc:", currentMainDoc);
        console.log("mainDocumentsModalOpen:", mainDocumentsModalOpen);
      }, 10);
    }
  };

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setCurrentDoc(null);
  }, []);

  const handleMainDocumentsModalClose = useCallback(() => {
    setMainDocumentsModalOpen(false);
    setCurrentMainDoc(null);
  }, []);

  const handleDocumentSelect = useCallback((doc) => {
    setCurrentDoc(doc);
    setModalOpen(true);
  }, []);

  // Handle modal upload complete - UPDATED to handle custom docs
  const handleModalUploadComplete = useCallback((uniqueKey, uploadedFiles) => {
    setUploadedDocuments((prev) => ({
      ...prev,
      [uniqueKey]: uploadedFiles,
    }));

    if (uploadedFiles.length > 0) {
      const firstFileUrl = uploadedFiles[0].serverUrl;
      const currentTime = new Date().toLocaleString();

      setUploadedDocs((prev) => ({
        ...prev,
        [uniqueKey]: firstFileUrl,
      }));

      setUploadTimes((prev) => ({
        ...prev,
        [uniqueKey]: currentTime,
      }));

      setUploadedFileNames((prev) => ({
        ...prev,
        [uniqueKey]: uploadedFiles.map((f) => f.name).join(", "),
      }));
    }
  }, []);

  // Handle file upload for additional documents
  const handleFileUpload = (event, docId) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!isAllowedDocumentFile(file)) {
      alert("Only PDF, JPG, JPEG, PNG, XLS, and XLSX files are allowed.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onloadend = async () => {
      const fileExtension = file.name.split(".").pop();
      const contentType = file.type;

      const fileName = `${claimNumber}_${docId}.${fileExtension}`;
      const blobName = `claimDocuments/${fileName}`;

      const base64String = reader.result.split(",")[1];

      setUploadedFileNames((prev) => ({
        ...prev,
        [docId]: fileName,
      }));

      try {
        setUploadingDocIds((prev) => [...prev, docId]);

        const response = await axios.post("/process_data/upload-file", {
          blobName,
          fileContent: base64String,
          contentType,
        });

        if (response.status === 200 || response.status === 201) {
          const uploadedUrl = response.data.url;
          const currentTime = new Date().toLocaleString();

          setUploadedDocs((prev) => ({ ...prev, [docId]: uploadedUrl }));
          setUploadTimes((prev) => ({ ...prev, [docId]: currentTime }));
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      } finally {
        setUploadingDocIds((prev) => prev.filter((id) => id !== docId));
      }
    };

    reader.readAsDataURL(file);
  };

  // Send completion email
  const sendCompletionEmail = async (auditId) => {
    try {
      const emailResponse = await axios.post(
        "/process_data/send-upload-complete-email",
        {
          audit_id: auditId,
          status: "upload_complete",
          message: "Claim Documents Upload Completed in Phase-2",
          step: "Document",
        },
      );

      if (emailResponse.status === 200 || emailResponse.status === 201) {
        console.log("Email notification sent successfully");
        setEmailSent(true);
      }
    } catch (error) {
      console.error("Error sending completion email:", error);
      return false;
    }
  };

  // Check status and start OCR after the completion updates are saved.
  const checkStatusAndStartOCR = async () => {
    const allInitialUploaded = areMainDocumentsUploaded;

    if (!allInitialUploaded || isProcessingOCR) {
      console.log("399", allInitialUploaded);
      return;
    }

    try {
      const response = await axios.post(
        "/process_data/get_survey_doc_status",
        { claimNumber },
        { timeout: 7200000 },
      );

      const surveyDocExists = response.data?.survey_doc;
      console.log("Survey doc exists?", surveyDocExists);

      setIsProcessingOCR(true);
      setProcessingError(null);
      setProcessingProgress(0);

      const claimFormData = await axios.get(
        `/process_data/get_claim_form_data/${claimNumber}`,
      );

      const claimFormDataRes = claimFormData.data.data;
      console.log("claim form data", claimFormDataRes);

      const city = claimFormDataRes?.workshopCity || "";
      console.log("City:", city);

      const responseProcessingOCR = await axios.post(
        "/process_data/initiate_document_processing",
        {
          claimNumber,
          city,
        },
      );

      console.log("processing status", responseProcessingOCR);
    } catch (err) {
      console.error("OCR process failed:", err);
      setProcessingError("OCR failed. Please try again.");
      setIsProcessingOCR(false);
    }
  };

  const handleDoneClick = async () => {
    try {
      console.log("DONE clicked, claimNumber:", claimNumber);
      setIsProcessingOCR(true);
      setProcessingError(null);
      setProcessingStage("Updating survey document...");
      setProcessingProgress(10);

      await axios.post(
        "/process_data/update_survey_doc",
        { claimNumber },
        { timeout: 720000 },
      );
      setSurveyDocUpdated(true);

      const now = new Date();
      const utcTime = now.toISOString();

      const res = await axios.post("/process_data/update-survey-completion", {
        claimId: claimNumber,
        timestamp: utcTime,
      });
      console.log("461:", utcTime);
      console.log("Survey completion API response:", res.data);

      if (claimNumber && !emailSent) {
        await sendCompletionEmail(claimNumber);
      }

      await checkStatusAndStartOCR();
      window.history.back();
    } catch (err) {
      console.error("Survey completion save failed:", err);
      setIsProcessingOCR(false);
      setProcessingError("Document completion failed. Please try again.");
      alert("Failed to save survey completion time");
    }
  };

  const addAdditionalDocument = () => {
    const newDoc = {
      id: `document-${additionalDocuments.length + 1}`,
      name: `Document ${additionalDocuments.length + 1}`,
    };
    setAdditionalDocuments([...additionalDocuments, newDoc]);
  };

  // Check how many custom documents are uploaded for a specific main document
  const countCustomDocumentsForDoc = (parentDocId) => {
    const customDocIds = [
      "document-1a",
      "document-2a",
      "document-3a",
      "document-4a",
      "document-5a",
    ];
    return customDocIds.filter((docId) => {
      const uniqueKey = `${parentDocId}_${docId}`;
      return uploadedDocs[uniqueKey] || uploadedDocuments[uniqueKey];
    }).length;
  };

  // Check if a main document has any custom documents
  const hasCustomDocuments = (parentDocId) => {
    return countCustomDocumentsForDoc(parentDocId) > 0;
  };

  // Render function for MAIN DOCUMENTS - UPDATED
  const renderMainDocumentCard = (doc) => {
    const customDocsCount = countCustomDocumentsForDoc(doc.id);
    const hasCustomDocs = customDocsCount > 0;

    // Check if main document is uploaded (main file या custom files)
    const isUploaded =
      uploadedDocs[doc.id] || uploadedDocuments[doc.id] || hasCustomDocs;

    return (
      <div className="document-card" key={doc.id}>
        <div className="text-content">
          <p className="document-name">{doc.name}</p>
          {/* {uploadedDocuments[doc.id] && (
            <p className='document-files-count'>
              {uploadedDocuments[doc.id].length} file(s)
            </p>
          )} */}
          {/* Show custom documents count for THIS document only */}
          {/* {hasCustomDocs && (
            <p className='custom-docs-count'>
              +{customDocsCount} custom documents
            </p>
          )} */}
        </div>

        <div className="action-buttons">
          {uploadedDocs[doc.id] ? (
            <button
              className="action-btn preview"
              onClick={() => handlePreviewClick(doc)}
            >
              Preview{" "}
              {uploadedDocuments[doc.id]?.length > 1
                ? `(${uploadedDocuments[doc.id].length})`
                : ""}
            </button>
          ) : uploadingDocIds.includes(doc.id) ? (
            <button className="action-btn uploading" disabled>
              <ImSpinner2 className="spinner" /> Uploading...
            </button>
          ) : (
            <button
              className="action-btn upload"
              onClick={() => handleUploadClick(doc)}
              disabled={isProcessingOCR}
            >
              <FaCloudUploadAlt /> Upload
            </button>
          )}

          {/* Separate button for custom documents */}
          {/* <button
            className='action-btn custom-docs upload'
            onClick={() => handleDocumentSelect(doc)}
            disabled={isProcessingOCR}
          >
            <FaCloudUploadAlt /> Add Custom Docs
          </button> */}
        </div>
      </div>
    );
  };

  // Render function for ADDITIONAL DOCUMENTS
  const renderAdditionalDocumentCard = (doc) => (
    <div className="document-card" key={doc.id}>
      <div className="text-content">
        <p className="document-name">{doc.name}</p>
      </div>

      {uploadedDocs[doc.id] ? (
        <button
          className="action-btn preview"
          onClick={() => handlePreviewClick(doc)}
        >
          Preview
        </button>
      ) : uploadingDocIds.includes(doc.id) ? (
        <button className="action-btn uploading" disabled>
          <ImSpinner2 className="spinner" /> Uploading...
        </button>
      ) : (
        <>
          <input
            type="file"
            id={`${doc.id}-hidden-input`}
            style={{ display: "none" }}
            onChange={(e) => handleFileUpload(e, doc.id)}
          />
          <button
            className="action-btn upload"
            onClick={() => handleUploadClick(doc)}
            disabled={isProcessingOCR}
          >
            <FaCloudUploadAlt /> Upload
          </button>
        </>
      )}
    </div>
  );

  const doesAnyVariantExist = async (docId) => {
    console.log(`Checking ANY variant for: ${docId}`);

    const variants = ["1a", "2a", "3a", "4a", "5a"];
    const extensions = ["pdf", "jpg", "jpeg", "png"];

    for (const variant of variants) {
      for (const ext of extensions) {
        // 🔹 both possible naming patterns
        const possibleNames = [
          `claimDocuments/${claimNumber}_${docId}_document-${variant}.${ext}`, // claim-form, policy
          `claimDocuments/${claimNumber}_${docId}-${variant}.${ext}`, // license, rc, workshop
        ];

        for (const blobName of possibleNames) {
          try {
            const res = await axios.post(
              "/process_data/check_document_exists",
              { blobName },
              { timeout: 3000 },
            );

            if (res.data.exists && res.data.url) {
              console.log(`✓ Found ${docId} → ${blobName}`);
              return res.data.url;
            }
          } catch (e) {}
        }
      }
    }

    console.log(`✗ No variants found for ${docId}`);
    return false;
  };

  const checkInitialDocsUploaded = async (initialDocuments) => {
    const result = {};
    console.log("1895", initialDocuments);
    await Promise.all(
      initialDocuments.map(async (doc) => {
        const fileUrl = await doesAnyVariantExist(doc.id);
        result[doc.id] = fileUrl;
      }),
    );

    console.log("UPLOAD STATUS:", result);
    return result;
  };
  useEffect(() => {
    let ignore = false;
    console.log("1908", initialDocuments);

    (async () => {
      const res = await checkInitialDocsUploaded(initialDocuments);
      console.log("1949", res);

      if (!ignore) setUploadedDocs(res);
    })();

    return () => {
      ignore = true;
    };
  }, []);

  // const allInitialUploaded = initialDocuments.every(doc => uploadedDocs[doc.id])
  const allInitialUploaded = areMainDocumentsUploaded;
  // console.log("1859",allInitialUploaded)
  const isProcessed = allInitialUploaded && docStatus === true;
  const isProcessing =
    allInitialUploaded && docStatus === false && docProcessingStatus === true;
  const canClickDone =
    allInitialUploaded && docStatus === false && docProcessingStatus === false;

  //   useEffect(() => {
  //   console.log('Upload status check:');
  //   initialDocuments.forEach(mainDoc => {
  //     const hasMain = uploadedDocs[mainDoc.id] || uploadedDocuments[mainDoc.id]
  //     const customDocIds = ['document-1a', 'document-2a', 'document-3a', 'document-4a', 'document-5a']
  //     const hasCustom = customDocIds.some(customId => {
  //       const uniqueKey = `${mainDoc.id}_${customId}`
  //       return uploadedDocs[uniqueKey] || uploadedDocuments[uniqueKey]
  //     })

  //     console.log(`${mainDoc.name}: Main=${hasMain}, Custom=${hasCustom}, Total=${hasMain || hasCustom}`);
  //   })
  //   console.log("1878",initialDocuments);
  //   console.log('All main docs uploaded?', allInitialUploaded);
  // }, [uploadedDocs, uploadedDocuments])
  useEffect(() => {
    console.log("Upload status check:");

    initialDocuments.forEach((mainDoc) => {
      const hasMain = uploadedDocs[mainDoc.id] || uploadedDocuments[mainDoc.id];

      const customDocIds = [
        "document-1a",
        "document-2a",
        "document-3a",
        "document-4a",
        "document-5a",
      ];

      const hasCustom = customDocIds.some((customId) => {
        const uniqueKey = `${mainDoc.id}_${customId}`;
        return uploadedDocs[uniqueKey] || uploadedDocuments[uniqueKey];
      });

      console.log(
        `${mainDoc.name}: Main=${hasMain}, Custom=${hasCustom}, Total=${hasMain || hasCustom}`,
      );
    });

    console.log("1878", initialDocuments);
    console.log("All main docs uploaded?", allInitialUploaded);
  }, [uploadedDocs, uploadedDocuments, initialDocuments, allInitialUploaded]);

  return (
    <div className="doc-content-survey">
      <div className="document-status-container-survey">
        <div className={`page-content ${isCheckingDocuments ? "blurred" : ""}`}>
          <div className="title-container ">
            <h2 className="title">Document Status</h2>
            <img
              src={
                "https://www.iconpacks.net/icons/1/free-document-icon-901-thumb.png"
              }
              alt="Logo"
              className="document-logo"
            />
          </div>

          <div className="documents-grid-survey">
            {initialDocuments.map(renderMainDocumentCard)}
          </div>

          <h3 className="additional-title">Additional Documents</h3>
          <div className="documents-grid-survey">
            {additionalDocuments.map(renderAdditionalDocumentCard)}
          </div>

          <button
            className="upload-additional"
            onClick={addAdditionalDocument}
            disabled={isProcessingOCR}
          >
            Add Additional Document
          </button>
        </div>

        {allInitialUploaded && (
          <div className="done-section">
            <button
              className="done-btn"
              onClick={handleDoneClick}
              disabled={
                docStatus === true ||
                (docStatus === false && docProcessingStatus === true) ||
                isProcessingOCR
              }
            >
              Done
            </button>

            {/* Status Messages */}
            <div className="status-messages">
              {docStatus === false && docProcessingStatus === true && (
                <div className="doc-status-message processing">
                  <ImSpinner2 className="spinner" />
                  <span>📄 Documents are processing...</span>
                </div>
              )}

              {docStatus === true && (
                <div className="doc-status-message processed">
                  <span>✅ Documents are processed</span>
                </div>
              )}
            </div>
          </div>
        )}

        {isCheckingDocuments && (
          <div className="checking-docs-overlay">
            <ImSpinner2 className="spinner" />
            <p>Fetching documents...</p>
          </div>
        )}

        <UploadModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          docId={currentDoc?.id}
          docName={currentDoc?.name}
          uploadedFiles={uploadedDocuments[currentDoc?.id] || []}
          onUploadComplete={handleModalUploadComplete}
          claimNumber={claimNumber}
        />

        <MainDocumentsModal
          key={currentMainDoc?.id} // Important: This ensures fresh state for each modal
          isOpen={mainDocumentsModalOpen}
          onClose={handleMainDocumentsModalClose}
          claimNumber={claimNumber}
          parentDocId={currentMainDoc?.id}
          parentDocName={currentMainDoc?.name}
          uploadedDocs={uploadedDocs}
          uploadedDocuments={uploadedDocuments}
          onUploadComplete={handleModalUploadComplete}
        />
      </div>
    </div>
  );
};

export default DocumentStatus;
